import React, { useState } from "react";
import Avatar from "../common/Avatar";
import {
  MESSAGE_REQUEST_INTENT_BY_KEY,
  MESSAGE_REQUEST_ORIGIN_BY_KEY,
} from "../../services/messageRequestApi";

// Relative age like the design ("2h ago" / "1d ago" / "2d ago"); falls back to a
// short date ("12 May") past a week — matching the Older-section rows in 1f.
export const formatRequestAge = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const IntentPill = ({ intentKey, mobile = false }) => {
  const intent = MESSAGE_REQUEST_INTENT_BY_KEY[intentKey];
  if (!intent) return null;
  return (
    <span
      className="rounded-full font-semibold"
      style={{
        padding: mobile ? "2px 9px" : "2px 8px",
        background: intent.pillBg,
        color: intent.pillText,
        fontSize: mobile ? "11px" : "10.5px",
      }}
    >
      {intent.label}
    </span>
  );
};

const OriginTag = ({ originKey, mobile = false }) => {
  const origin = MESSAGE_REQUEST_ORIGIN_BY_KEY[originKey];
  if (!origin) return null;
  // "Direct" reads on a tinted chip; the "via …" origins on a plain white chip
  // (design 1f card).
  const isDirect = originKey === "direct";
  return (
    <span
      className="rounded-[6px] font-medium"
      style={{
        padding: mobile ? "2px 8px" : "2px 7px",
        background: isDirect ? "#F8FAFC" : "#FFFFFF",
        border: "1px solid #E6EAF0",
        color: "#667085",
        fontSize: mobile ? "11px" : "10.5px",
      }}
    >
      {origin.tagLabel}
    </span>
  );
};

// MessageRequestCard — one pending message request (design frame 1f). Person-
// first per D18: avatar + name + agency, then the Agent role tag + intent pill +
// origin tag, the first message as a 2-line-clamped payload, and the actions.
//
// TWO DIRECTIONS, ONE ANATOMY:
//   variant="received" (default) — the gate's inbox. Counterpart = request.sender,
//     actions = Accept / Ignore.
//   variant="sent" — the SENDER's own pending list (the list frame 1r's "View your
//     N pending requests" opens). Counterpart = request.recipient, and the single
//     action is Withdraw — the sender taking their own request back. There is
//     deliberately NO status element beyond pending: the sender's vocabulary is
//     exactly two words, pending and accepted (frame 1k), and this list is
//     filtered to pending, so a "declined"/"ignored"/"expired" label can never
//     appear here.
//
// Props:
//   request   MessageRequestReceivedSerializer / …SenderSerializer shape:
//             { id, intent, origin, sender|recipient:{name,agency,
//               profile_picture_url?}, message, created_at, status, channel_cid }
//   selected  bool  — highlights the row (used by the two-pane 1f layout).
//   onSelect  (request) => void   — open the detail pane / mark active.
//   onAccept  (id) => Promise     — resolves so the card can show its busy state.
//   onIgnore  (id) => Promise     — optimistic removal is the PARENT's job (1g:
//                                   "card silently leaves"); this only awaits.
//   onWithdraw (id) => Promise    — variant="sent" only; same contract as above.
//   t         i18n passthrough (defaults to identity for verbatim copy).
const MessageRequestCard = ({
  request,
  selected = false,
  variant = "received",
  onSelect,
  onAccept,
  onIgnore,
  onWithdraw,
  mobile = false,
  t = (s) => s,
}) => {
  const [busy, setBusy] = useState(false);
  if (!request) return null;

  const isSent = variant === "sent";
  // The counterpart is whoever ISN'T the viewer: the sender on a received card,
  // the recipient on a sent one.
  const counterpart = (isSent ? request.recipient : request.sender) || {};
  const name = counterpart.name || t("Agent");
  const agency = counterpart.agency || "";

  const doAccept = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await onAccept?.(request.id);
    } finally {
      setBusy(false);
    }
  };
  const doIgnore = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await onIgnore?.(request.id);
    } finally {
      setBusy(false);
    }
  };
  const doWithdraw = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await onWithdraw?.(request.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect?.(request)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onSelect?.(request);
      }}
      className="flex cursor-pointer flex-col"
      style={{
        gap: mobile ? 9 : 8,
        padding: mobile ? "13px 14px" : "12px 13px",
        borderRadius: mobile ? 14 : 12,
        // Non-selected border matches the frame markup (#E9EDF2); selected uses
        // the blue rail treatment (1f).
        border: `1px solid ${selected ? "#B9D6FF" : "#E9EDF2"}`,
        background: selected ? "#F7FAFF" : "#FFFFFF",
        boxShadow: selected ? "inset 2px 0 0 #2283FF" : "none",
      }}
    >
      <div className="flex items-center gap-2.5">
        <Avatar
          src={counterpart.profile_picture_url}
          alt={name}
          fallbackText={name}
          size={mobile ? 44 : 40}
        />
        <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 1 }}>
          <span
            className="truncate font-bold text-[#101828]"
            style={{ fontSize: mobile ? "14.5px" : "13.5px" }}
          >
            {name}
          </span>
          {agency && (
            <span
              className="truncate text-[#667085]"
              style={{ fontSize: mobile ? "12px" : "11.5px" }}
            >
              {agency}
            </span>
          )}
        </div>
        <span
          className="flex-shrink-0 text-[#98A2B3]"
          style={{ fontSize: mobile ? "11.5px" : "11px" }}
        >
          {formatRequestAge(request.created_at)}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <span
          className="rounded-[6px] font-semibold"
          style={{
            padding: "1px 6px",
            background: "#F0F6FF",
            border: "1px solid #D6E6FF",
            color: "#1466D8",
            fontSize: mobile ? "10.5px" : "10px",
          }}
        >
          {t("Agent")}
        </span>
        <IntentPill intentKey={request.intent} mobile={mobile} />
        <OriginTag originKey={request.origin} mobile={mobile} />
      </div>

      <div
        className="overflow-hidden leading-[1.45] text-[#475467]"
        style={{
          fontSize: mobile ? "13px" : "12.5px",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        {request.message}
      </div>

      {isSent ? (
        // Sender side: ONE action, the sender's own (frame 1r's copy names it —
        // "New sends unlock as agents accept — or as you withdraw a pending
        // request"). No Accept/Ignore: those belong to the recipient.
        <div className="flex" style={{ gap: mobile ? 9 : 8 }}>
          <button
            type="button"
            onClick={doWithdraw}
            disabled={busy}
            className="flex-1 text-center font-semibold text-[#475467] disabled:opacity-60"
            style={{
              padding: mobile ? "11px 0" : "7px 0",
              borderRadius: mobile ? 10 : 8,
              fontSize: mobile ? "14px" : "12.5px",
              background: "#FFFFFF",
              border: "1px solid #E4E9F0",
            }}
          >
            {busy ? t("Withdrawing…") : t("Withdraw")}
          </button>
        </div>
      ) : (
      <div className="flex" style={{ gap: mobile ? 9 : 8 }}>
        <button
          type="button"
          onClick={doAccept}
          disabled={busy}
          className="flex-1 text-center font-semibold text-white disabled:opacity-60"
          style={{
            padding: mobile ? "11px 0" : "7px 0",
            borderRadius: mobile ? 10 : 8,
            fontSize: mobile ? "14px" : "12.5px",
            background: "#2283FF",
          }}
        >
          {busy ? t("Accepting…") : t("Accept")}
        </button>
        <button
          type="button"
          onClick={doIgnore}
          disabled={busy}
          className="flex-1 text-center font-semibold text-[#475467] disabled:opacity-60"
          style={{
            padding: mobile ? "11px 0" : "7px 0",
            borderRadius: mobile ? 10 : 8,
            fontSize: mobile ? "14px" : "12.5px",
            background: "#FFFFFF",
            border: "1px solid #E4E9F0",
          }}
        >
          {t("Ignore")}
        </button>
      </div>
      )}
    </div>
  );
};

export default MessageRequestCard;
